import PropTypes from 'prop-types';
import { Box, Button, Chip, Paper, Stack, Typography, alpha, useMediaQuery, useTheme } from '@mui/material';

import Iconify from '../../../../components/Iconify';
import { useCoach } from '../../../../onboarding/engine/CoachProvider';
import { getGuide } from '../../../../onboarding/guides';
import { useAdminOnboarding } from '../../../../contexts/AdminOnboardingContext';
import { GUIDE_STATUS, isGuideDone, normalizeProgress } from '../../../../onboarding/progress';

// ---------------------------------------------------------------------------
// The button at the end of "shall I show you?".
//
// Dex answers a how-to question, then offers the walkthrough that covers it.
// Pressing this hands the user to the same engine the launcher and the
// dashboard card use, so a guide started from a chat is not a second, lesser
// kind of tour: it navigates, highlights and advances exactly the same way.
//
// The offer is only ever an offer. Nothing starts on render, because a page
// that moves under somebody mid-sentence reads as a bug no matter how good the
// tour is.
// ---------------------------------------------------------------------------

function isResumable(progress) {
  return progress.status === GUIDE_STATUS.inProgress || progress.status === GUIDE_STATUS.paused;
}

export default function GuideOfferBlock({ block }) {
  const theme = useTheme();
  const isPhone = useMediaQuery('(max-width:599.95px)');
  const coach = useCoach();
  const { guidesProgress } = useAdminOnboarding();

  const guideId = block?.guide_id;
  // The backend catalog is generated from this registry and CI keeps the two in
  // step, but a backend deployed ahead of the frontend can still name a guide
  // this build has never heard of. A card whose button does nothing is worse
  // than no card, so drop it and leave Dex's written answer standing.
  const guide = guideId ? getGuide(guideId) : null;
  if (!guide) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[coach] Dex offered unknown guide "${guideId}"`);
    }
    return null;
  }

  // The backend catalog filters offers by the caller's codes, so this should
  // never fire. It is here because the model chooses which guide to name and a
  // card that refuses when pressed is a worse answer than no card: Dex's
  // written explanation still stands on its own.
  if (!coach.canRun(guideId)) return null;

  const progress = normalizeProgress(guidesProgress?.[guideId]);
  const done = isGuideDone(progress);
  const resumable = isResumable(progress);

  // Desktop-only guides point at controls a phone never paints. Saying so up
  // front beats stalling three steps in with no target on screen.
  const blockedOnPhone = isPhone && (block.desktop_only || guide.desktopOnly);

  const label = (() => {
    if (resumable) return 'Pick up where I left off';
    if (done) return 'Take it again';
    return 'Show me';
  })();

  const handleStart = () => {
    coach.start(guideId, { at: resumable ? progress.current_step || 0 : 0 });
  };

  const meta = [
    block.minutes ? `${block.minutes} min` : null,
    block.steps ? `${block.steps} step${block.steps === 1 ? '' : 's'}` : null,
  ].filter(Boolean);

  return (
    <Paper
      variant="outlined"
      sx={{
        my: 1.5,
        p: 2,
        borderRadius: 2,
        maxWidth: 440,
        borderColor: alpha(theme.palette.primary.main, 0.35),
        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.04),
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Box
            sx={{
              width: 34, height: 34, borderRadius: 1.25, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.14),
            }}
          >
            <Iconify
              icon={done ? 'mdi:check-bold' : 'mdi:gesture-tap'}
              sx={{ width: 18, height: 18, color: 'primary.main' }}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
              <Typography variant="subtitle2" fontWeight={700}>
                {block.title || guide.title}
              </Typography>
              {done && (
                <Chip
                  size="small"
                  label="Done"
                  sx={{
                    height: 18, fontSize: 10, fontWeight: 700,
                    color: 'success.dark', bgcolor: alpha(theme.palette.success.main, 0.16),
                  }}
                />
              )}
              {resumable && (
                <Chip
                  size="small"
                  label="In progress"
                  sx={{
                    height: 18, fontSize: 10, fontWeight: 700,
                    color: 'info.dark', bgcolor: alpha(theme.palette.info.main, 0.16),
                  }}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {block.summary || guide.summary}
            </Typography>
          </Box>
        </Stack>

        {block.reason && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {block.reason}
          </Typography>
        )}

        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
          {meta.length > 0 && (
            <Typography variant="caption" color="text.disabled">
              {meta.join(' · ')}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          {blockedOnPhone ? (
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
              Open DeskOS on a computer to run this one.
            </Typography>
          ) : (
            <Button
              variant="contained"
              size="small"
              startIcon={<Iconify icon="mdi:play" />}
              onClick={handleStart}
            >
              {label}
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

GuideOfferBlock.propTypes = {
  block: PropTypes.object.isRequired,
};
